import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useQuestionnaireStore from '../store/useQuestionnaireStore';
import useWarning from '../hooks/useWarningModal';
import { ChevronLeft, ArrowUpCircle, ArrowDownCircle, ChevronDown, Check } from 'lucide-react';
import Button from '../components/common/Button';

// --- DATA SOURCE ---
const factorsData = {
    "Shoulder": {
        aggravating: ["Overhead reaching", "Lifting weights", "Dressing", "Throwing", "Lying on painful side"],
        relieving: ["Rest", "Heat", "Avoiding overhead activity", "Supported arm position"]
    },
    "Elbow": { 
        aggravating: ["Repetitive gripping", "Typing", "Lifting", "Racquet sports"],
        relieving: ["Rest", "Activity modification", "Ice/Heat", "Counterforce brace"]
    },
    "Wrist": { 
        aggravating: ["Typing", "Writing", "Lifting", "Pushing", "Repetitive use (texting, mouse)"],
        relieving: ["Splinting", "Rest", "Posture correction", "Ergonomic tools"]
    },
    "Hip": {
        aggravating: ["Prolonged sitting", "Running", "Squatting", "Stair climbing"],
        relieving: ["Rest", "Activity modification", "Stretching", "Heat"]
    },
    "Knee": {
        aggravating: ["Jumping", "Running", "Stairs", "Squatting", "Prolonged sitting"],
        relieving: ["Rest", "Isometric exercise", "Ice/Heat", "Taping", "Quad stretching"]
    },
    "Ankle": {
        aggravating: ["Running", "Jumping", "Uneven surfaces", "Sudden stops/turns"],
        relieving: ["Rest", "Elevation", "Ice", "Supportive footwear"]
    },
    "Back": {
        aggravating: [
            "Prolonged sitting", "Bending forward", "Lifting heavy objects", "Twisting spine",
            "Prolonged standing", "Poor posture / Slouching", "Coughing or sneezing"
        ],
        relieving: [
            "Rest / Lying down", "Extension exercises", "Lumbar support", "Gentle walking",
            "Heat / Warm shower", "Posture breaks"
        ]
    },
    "Neck": {
        aggravating: [
            "Looking down at phone / screen", "Prolonged desk work", "Sudden neck turns",
            "Carrying heavy backpack / shoulder bag", "Sleeping on awkward pillow", "Driving long hours"
        ],
        relieving: [
            "Rest", "Neck support pillow", "Gentle neck stretches / Chin tucks",
            "Heat / Ice pack", "Posture correction breaks", "Ergonomic screen height"
        ]
    },
    "Spine": {
        aggravating: [
            "Prolonged sitting at computer", "Looking down at phone", "Sudden neck movements", 
            "Carrying load on shoulder", "Prolonged desk work", "Poor posture", "Carrying backpack",
            "Prolonged standing", "Lifting", "Bending forward", "Twisting"
        ],
        relieving: [
            "Rest", "Posture correction", "Neck support pillow", "Gentle mobility/stretch",
            "Extension exercises", "Posture breaks", "Foam rolling", "Lumbar support"
        ]
    }
};

const Step8_Factors = () => {
    const navigate = useNavigate();
    const { 
        painLocation, 
        aggravatingFactor, 
        setAggravatingFactor,
        relievingFactor, 
        setRelievingFactor 
    } = useQuestionnaireStore();
    
    const showWarning = useWarning();
    const relievingSectionRef = useRef(null);

    // Ensure we are working with arrays (safe fallback)
    const selectedAggravating = Array.isArray(aggravatingFactor) ? aggravatingFactor : [];
    const selectedRelieving = Array.isArray(relievingFactor) ? relievingFactor : [];

    const currentJoint = painLocation || "Shoulder";
    const dataKey = factorsData[currentJoint] ? currentJoint : "Spine"; 
    const currentData = factorsData[dataKey];

    // Toggle Logic for Multi-Select
    const toggleAggravating = (factor) => {
        if (selectedAggravating.includes(factor)) {
            setAggravatingFactor(selectedAggravating.filter(f => f !== factor));
        } else {
            setAggravatingFactor([...selectedAggravating, factor]);
        }
    };

    const toggleRelieving = (factor) => {
        if (selectedRelieving.includes(factor)) {
            setRelievingFactor(selectedRelieving.filter(f => f !== factor));
        } else {
            setRelievingFactor([...selectedRelieving, factor]);
        }
    };

    const scrollToRelieving = () => {
        relievingSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleNext = () => {
        if (selectedAggravating.length === 0) {
            showWarning("Please select at least one aggravating factor.");
            return;
        }
        if (selectedRelieving.length === 0) {
            showWarning("Please select at least one relieving factor.");
            return;
        }
        
        console.log("Aggravating:", selectedAggravating);
        console.log("Relieving:", selectedRelieving);
        navigate('/step-9');
    };

    return (
        <motion.div
            className="page step8-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <div className="step-header">
                <button onClick={() => navigate(-1)} className="back-button">
                    <ChevronLeft size={24} /> Back
                </button>
                <h2>Trigger Factors for <strong>{currentJoint}</strong></h2>
            </div>

            <div className="factors-layout">
                
                {/* Section 1: Aggravating Factors */}
                <section className="factor-section">
                    <div className="section-title">
                        <ArrowUpCircle className="icon-aggr" size={24} />
                        <h3>Which activities cause you the most pain?</h3>
                        <span className="subtitle">(Select all that apply)</span>
                    </div>
                    <div className="options-grid">
                        {currentData.aggravating.map((factor) => {
                            const isSelected = selectedAggravating.includes(factor);
                            return (
                                <button
                                    key={factor}
                                    className={`factor-pill ${isSelected ? 'selected aggr' : ''}`}
                                    onClick={() => toggleAggravating(factor)}
                                >
                                    {factor}
                                    {isSelected && <Check size={16} className="check-icon" />}
                                </button>
                            );
                        })}
                    </div>
                    
                    {/* Navigation Icon to Next Question with Animation */}
                    <div className="scroll-hint-wrapper">
                        <motion.button 
                            className="scroll-down-btn" 
                            onClick={scrollToRelieving} 
                            aria-label="Go to next question"
                            animate={{ y: [0, 8, 0] }}
                            transition={{ 
                                duration: 1.5, 
                                repeat: Infinity, 
                                ease: "easeInOut" 
                            }}
                        >
                            <ChevronDown size={24} />
                        </motion.button>
                    </div>
                </section>

                {/* Section 2: Relieving Factors */}
                <section className="factor-section" ref={relievingSectionRef}>
                    <div className="section-title">
                        <ArrowDownCircle className="icon-relieve" size={24} />
                        <h3>Which activities ease the pain?</h3>
                        <span className="subtitle">(Select all that apply)</span>
                    </div>
                    <div className="options-grid">
                        {currentData.relieving.map((factor) => {
                            const isSelected = selectedRelieving.includes(factor);
                            return (
                                <button
                                    key={factor}
                                    className={`factor-pill ${isSelected ? 'selected relieve' : ''}`}
                                    onClick={() => toggleRelieving(factor)}
                                >
                                    {factor}
                                    {isSelected && <Check size={16} className="check-icon" />}
                                </button>
                            );
                        })}
                    </div>
                </section>

            </div>

            <div className="navigation-buttons">
                <Button onClick={handleNext}>Next</Button>
            </div>
        </motion.div>
    );
};

export default Step8_Factors;